import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sortByLabel, sortByName, sortByTitle, sortStrings } from "@/lib/sortRows";
import { todayISO } from "@/lib/localDate";
import AppLayout from "@/components/AppLayout";
import { PriorityBadge, StatusPill, STATUS_STYLES } from "@/components/StatusBadge";
import AvatarStack from "@/components/AvatarStack";
import DataTable from "@/components/DataTable";
import SlippageCell from "@/components/SlippageCell";
import { DONE_PROJECT_STATUSES, matchesSchedule, SCHEDULE_FILTERS, slippageSortValue } from "@/lib/ageing";
import TableSearch, { matchesQuery } from "@/components/TableSearch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, UploadSimpleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReportIssueModal from "@/components/ReportIssueModal";
import BulkUploadProjectsModal from "@/components/BulkUploadProjectsModal";

const P_STATUSES = ["initiation", "planning", "execution", "closed", "hold"];

// Same column model as the Tasks grid. `cls` carries padding AND width so both
// live in one place; a column with no width is elastic and splits whatever the
// fixed columns leave, which is what makes hiding a column widen the text
// columns instead of just adding whitespace.
//
// `defaultHidden` matters more here than on Tasks: fifteen columns cannot fit a
// screen at once, so the stakeholder-tracker extras start switched off and the
// picker brings them back. That choice then persists per browser.
const COLUMN_DEFS = [
  { key: "entity",      label: "Entity",                cls: "px-3", weight: 112 },
  { key: "priority",    label: "Priority",              cls: "px-3", weight: 100 },
  // Key stays "task_number" deliberately: DataTable intersects a saved column
  // layout against these keys, so renaming it would hide the column for every
  // user who already has prefs in localStorage. The column now shows how many
  // tasks the project has, not the free-text tracker field.
  { key: "task_number", label: "Tasks",                 cls: "px-3 text-right", weight: 88 },
  { key: "initiative",  label: "Initiative",            cls: "px-3", weight: 168 },
  { key: "project",     label: "Project / Description", head: "Project", locked: true,
    // Sticky so it survives horizontal scroll through Status/ETA/Cost/Manager/
    // Slippage — bg-white/group-hover:bg-zinc-50 keep it opaque and in step
    // with the row's own hover tint (see DataTable.jsx's `group` on <tr>), and
    // z-10 keeps later (non-sticky) columns from painting over it once they
    // scroll underneath. minWidth stops it being squeezed as more columns are
    // switched on via "Show columns".
    cls: "px-4 sticky left-0 z-10 bg-white group-hover:bg-zinc-50",
    weight: 240, minWidth: 220,
    sortValue: (p) => p.title || "" },
  { key: "risk",        label: "Risk / Challenges",     head: "Risk", cls: "px-4", defaultHidden: true },
  { key: "tech_team",   label: "Tech Team",             cls: "px-3", weight: 124 },
  { key: "func_team",   label: "Func Team",             cls: "px-3", weight: 124 },
  { key: "status",      label: "Status",                cls: "px-3", weight: 128 },
  /*
    Sorting was not enabled on this grid at all — no column declared a
    sortValue, so every header click did nothing. The three schedule columns
    get one, which is what makes "sort by ageing to see the worst first"
    possible. ISO date strings compare correctly as text, and DataTable parks
    blanks last in both directions.

    A project's original commitment is end_date. There is no projects.eta
    column; assuming there was one silently broke the weekly digest once.
  */
  { key: "eta",         label: "ETA",                   cls: "px-3", weight: 104,
    sortValue: (p) => p.end_date || "" },
  { key: "revised",     label: "Revised ETA",           cls: "px-3", weight: 124,
    sortValue: (p) => p.revised_eta || "" },
  /*
    Key is "slippage", not "ageing": this column has never been deployed, so no
    saved column layout in anyone's browser mentions either name and renaming
    the key costs nothing. (Contrast task_number above, which is stuck with its
    old key precisely because it HAS shipped.)
  */
  { key: "slippage",    label: "Slippage",              cls: "px-3 text-right", weight: 124,
    // First click = most overdue first, which is now the most NEGATIVE
    // days-left figure, so this sorts ASCENDING first.
    sortDefaultDir: "asc",
    sortHint: "Click to sort most overdue first",
    sortValue: (p) => slippageSortValue({ eta: p.end_date, revised: p.revised_eta, done: projectDone(p) }) },
  { key: "fy_lic_cost", label: "FY Lic Cost $",         cls: "px-3 text-right", weight: 124, defaultHidden: true },
  { key: "imp_cost",    label: "Imp Cost $",            cls: "px-3 text-right", weight: 116, defaultHidden: true },
  { key: "comments",    label: "Comments",              cls: "px-4", defaultHidden: true },
  { key: "manager",     label: "Manager",               cls: "px-3", weight: 104 },
];

// A project is finished when it is closed. Unlike a task it has no `live`
// state and no completion timestamp, so "finished" is all that can be said
// about it — see lib/ageing.js.
const projectDone = (p) => DONE_PROJECT_STATUSES.includes(p.status);

const COL_STORAGE_KEY = "projects.visibleColumns";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const money = (v) => (v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "—");
const P_PRIORITIES = ["high", "medium", "low"];

export default function ProjectsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const isManager = user?.role === "manager" || user?.role === "owner";
  const [items, setItems] = useState([]);
  // project_id -> number of tasks, for the Tasks column. Counted client-side
  // from the task list because there is no server to aggregate on; the count is
  // therefore of tasks the viewer is allowed to see, same as the Tasks page.
  const [taskCounts, setTaskCounts] = useState({});
  const [allTasks, setAllTasks] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [initiativeFilter, setInitiativeFilter] = useState("all");
  // Sits after Initiative in the cascade (Entity -> Initiative -> Project ->
  // Status) and before the independent Schedule filter — a project is more
  // specific than an initiative, so it narrows further, the same direction
  // the other two already narrow in.
  const [projectFilter, setProjectFilter] = useState("all");
  // Deliberately OUTSIDE the Entity -> Initiative -> Status cascade: it asks
  // about dates, not about where a project sits, so narrowing it by the others
  // (or resetting it when they change) would only hide rows the reader is
  // hunting for.
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [issueModal, setIssueModal] = useState({ open: false, entityId: null, entityLabel: "" });
  const emptyForm = () => ({
    title: "", description: "", plan: "", status: "initiation", initiative_id: "",
    priority: "medium", owner_id: (user?.role === "manager" || user?.role === "owner") ? user.id : "",
    start_date: todayISO(), end_date: "",
    // Stakeholder tracker columns — Entity/Priority/Task Number/Description/
    // Risk-Challenges/Tech Team/Func Team/Status/ETA/Revised ETA/FY Lic Cost/
    // Imp Cost/Comments. Priority/Description/Status/ETA already existed.
    entity: "", task_number: "", risk_challenges: "", tech_team: "", func_team: "",
    revised_eta: "", fy_lic_cost: "", imp_cost: "", comments: "",
  });
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [p, i, u, t] = await Promise.all([api.get("/projects"), api.get("/initiatives"), api.get("/users"), api.get("/tasks")]);
    setItems(p.data);
    const counts = {};
    (t.data || []).forEach((task) => { counts[task.project_id] = (counts[task.project_id] || 0) + 1; });
    setTaskCounts(counts);
    setAllTasks(t.data || []);
    setInitiatives(sortByTitle(i.data));
    setUsers(sortByName(u.data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.initiative_id) { toast.error("Select an initiative"); return; }
    if (!form.owner_id) { toast.error("Select a project manager"); return; }
    try {
      await api.post("/projects", {
        ...form,
        owner_id: form.owner_id,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        revised_eta: form.revised_eta || null,
        fy_lic_cost: form.fy_lic_cost === "" ? null : Number(form.fy_lic_cost),
        imp_cost: form.imp_cost === "" ? null : Number(form.imp_cost),
      });
      toast.success("Project created");
      setForm(emptyForm());
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const iMap = Object.fromEntries(initiatives.map((i) => [i._id, i.title]));
  const uMap = Object.fromEntries(users.map((u) => [u._id, u.name || u.email]));
  const filtered = items
    .filter((p) => filter === "all" || p.status === filter)
    .filter((p) => entityFilter === "all" || p.entity === entityFilter)
    .filter((p) => initiativeFilter === "all" || p.initiative_id === initiativeFilter)
    .filter((p) => projectFilter === "all" || p._id === projectFilter)
    .filter((p) => matchesSchedule(scheduleFilter, {
      eta: p.end_date, revised: p.revised_eta, done: projectDone(p),
    }))
    // Search runs last, over the same text the row puts on screen (including
    // the initiative title, which the row shows resolved rather than as an id)
    // so anything a reader can see, they can search for.
    .filter((p) =>
      matchesQuery(
        query,
        p.title, p.description, p.entity, p.risk_challenges,
        p.tech_team, p.func_team, p.comments, iMap[p.initiative_id]
      )
    );
  const uById = Object.fromEntries(users.map((u) => [u._id, { id: u._id, name: u.name || u.email, avatar_url: u.avatar_url }]));
  const managers = users.filter((u) => u.role === "manager" || u.role === "owner");

  // Full cascade: Entity → Initiative → Project → Status. Entity is
  // independent (top of the hierarchy — every project's own entity, straight
  // off the full list). Initiative only offers initiatives that actually have
  // a project under the selected entity. Project only offers projects that
  // actually occur under the selected entity + initiative — a project IS one
  // of the rows, so this one narrows to a single row rather than a group of
  // them. Status only offers statuses that actually occur among projects
  // matching entity + initiative + project — same "each filter narrows the
  // next" cascade as the Tasks page. Schedule stays outside this chain (see
  // its own state above) — it asks about dates, not about where a project
  // sits.
  const entityFilterOptions = [...new Set(items.map((p) => p.entity).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );

  const projectsMatchingEntity = (e) => items.filter((p) => e === "all" || p.entity === e);
  const projectsMatchingEntityAndInitiative = (e, i) =>
    projectsMatchingEntity(e).filter((p) => i === "all" || p.initiative_id === i);
  const projectsMatchingUpTo = (e, i, pr) =>
    projectsMatchingEntityAndInitiative(e, i).filter((p) => pr === "all" || p._id === pr);

  const initiativeFilterOptions = (entityFilter === "all"
    ? initiatives
    : initiatives.filter((i) => projectsMatchingEntity(entityFilter).some((p) => p.initiative_id === i._id))
  ).slice().sort((a, b) => a.title.localeCompare(b.title));

  const projectFilterOptions = projectsMatchingEntityAndInitiative(entityFilter, initiativeFilter)
    .slice().sort((a, b) => a.title.localeCompare(b.title));

  const statusFilterOptions = [...new Set(projectsMatchingUpTo(entityFilter, initiativeFilter, projectFilter).map((p) => p.status))]
    .filter((s) => P_STATUSES.includes(s))
    .sort((a, b) => (STATUS_STYLES[a]?.label || a).localeCompare(STATUS_STYLES[b]?.label || b));

  // Resets projectFilter when it no longer belongs under the entity/initiative
  // just chosen, and returns the value to actually use for the immediate
  // status-validity check below (state updates from this same tick haven't
  // landed yet when that check runs).
  const reconcileProjectFilter = (e, i) => {
    const stillValid = projectFilter === "all" || projectsMatchingEntityAndInitiative(e, i).some((p) => p._id === projectFilter);
    if (!stillValid) setProjectFilter("all");
    return stillValid ? projectFilter : "all";
  };

  const onEntityChange = (v) => {
    setEntityFilter(v);
    const stillValidInitiative = initiativeFilter === "all" || projectsMatchingEntity(v).some((p) => p.initiative_id === initiativeFilter);
    const nextInitiative = stillValidInitiative ? initiativeFilter : "all";
    if (!stillValidInitiative) setInitiativeFilter("all");
    const nextProject = reconcileProjectFilter(v, nextInitiative);
    if (filter !== "all" && !projectsMatchingUpTo(v, nextInitiative, nextProject).some((p) => p.status === filter)) {
      setFilter("all");
    }
  };

  const onInitiativeChange = (v) => {
    setInitiativeFilter(v);
    const nextProject = reconcileProjectFilter(entityFilter, v);
    if (filter !== "all" && !projectsMatchingUpTo(entityFilter, v, nextProject).some((p) => p.status === filter)) {
      setFilter("all");
    }
  };

  const onProjectChange = (v) => {
    setProjectFilter(v);
    if (filter !== "all" && !projectsMatchingUpTo(entityFilter, initiativeFilter, v).some((p) => p.status === filter)) {
      setFilter("all");
    }
  };


  const renderCell = (key, p) => {
    switch (key) {
      case "entity":
        return <div className="break-words text-zinc-600" title={p.entity || ""}>{p.entity || "—"}</div>;
      case "priority":
        return <PriorityBadge priority={p.priority} />;
      case "task_number":
        return <span className="font-mono text-xs tabular-nums text-zinc-600">{taskCounts[p._id] || 0}</span>;
      case "project":
        return (
          <div className="min-w-0">
            <Link to={`/projects/${p._id}`} className="block break-words font-medium hover:text-[#0176D3]" title={p.title}>{p.title}</Link>
            {p.description && <div className="text-xs break-words text-zinc-500" title={p.description}>{p.description}</div>}
          </div>
        );
      case "risk":
        return <div className="break-words text-zinc-600" title={p.risk_challenges || ""}>{p.risk_challenges || "—"}</div>;
      case "tech_team":
        return <div className="break-words text-zinc-600" title={p.tech_team || ""}>{p.tech_team || "—"}</div>;
      case "func_team":
        return <div className="break-words text-zinc-600" title={p.func_team || ""}>{p.func_team || "—"}</div>;
      case "status":
        return <StatusPill status={p.status} />;
      case "eta":
        return <span className="font-mono text-xs text-zinc-600">{fmtDate(p.end_date)}</span>;
      case "revised": {
        // Same slip rule as the Tasks grid: a revised date past the one the
        // project was committed to. ETA here is end_date.
        const eta = p.end_date ? new Date(p.end_date) : null;
        const revised = p.revised_eta ? new Date(p.revised_eta) : null;
        const slipped = Boolean(eta && revised && revised > eta);
        return (
          <span
            className="inline-flex items-center gap-1.5"
            title={slipped ? `Slipped past its ETA of ${eta.toLocaleDateString()}` : undefined}
          >
            {slipped && <span className="w-1.5 h-1.5 rounded-full bg-zinc-800 shrink-0" />}
            <span className={`font-mono text-xs ${slipped ? "font-semibold text-zinc-900" : "text-zinc-600"}`}>
              {fmtDate(p.revised_eta)}
            </span>
          </span>
        );
      }
      case "slippage":
        // A project's original commitment is end_date, and it has no
        // completion timestamp at all — so a closed project reports its name
        // and nothing more, which is all it honestly can.
        return <SlippageCell status={p.status} eta={p.end_date} revised={p.revised_eta} />;
      case "fy_lic_cost":
        return <span className="font-mono text-xs tabular-nums text-zinc-600">{money(p.fy_lic_cost)}</span>;
      case "imp_cost":
        return <span className="font-mono text-xs tabular-nums text-zinc-600">{money(p.imp_cost)}</span>;
      case "comments":
        return <div className="break-words text-zinc-600" title={p.comments || ""}>{p.comments || "—"}</div>;
      case "initiative":
        return <div className="break-words text-zinc-600" title={iMap[p.initiative_id] || ""}>{iMap[p.initiative_id] || "—"}</div>;
      case "manager": {
        const m = uById[p.owner_id];
        return <AvatarStack people={m ? [m] : []} limit={1} empty="—" />;
      }
      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <div className="sticky top-0 z-20 px-8 py-4 trk-sticky-header">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="trk-uppercase-label">All</div>
            <h1 className="text-3xl font-extrabold tracking-tight font-display">Projects</h1>
          </div>
          {(isAdmin || isManager) && (
          <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid="bulk-upload-projects-button"
            className="rounded-sm"
            onClick={() => setBulkOpen(true)}
          >
            <UploadSimpleIcon size={14} weight="bold" className="mr-1" /> Bulk Upload
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-project-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                <PlusIcon size={14} weight="bold" className="mr-1" /> New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Create Project</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Initiative <span className="text-red-600">*</span></Label>
                    <Select value={form.initiative_id} onValueChange={(v) => setForm({ ...form, initiative_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="project-initiative-select"><SelectValue placeholder="Select initiative" /></SelectTrigger>
                      <SelectContent>
                        {initiatives.map((i) => <SelectItem key={i._id} value={i._id}>{i.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Entity</Label>
                    <Input value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} placeholder="e.g. Indorama Nigeria" className="rounded-sm" data-testid="project-entity-input" />
                  </div>
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Title <span className="text-red-600">*</span></Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="project-title-input" />
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" rows={2} data-testid="project-description-input" />
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Risk / Challenges</Label>
                  <Textarea value={form.risk_challenges} onChange={(e) => setForm({ ...form, risk_challenges: e.target.value })} className="rounded-sm" rows={2} data-testid="project-risk-input" />
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Plan</Label>
                  <Textarea value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="rounded-sm" rows={3} data-testid="project-plan-input" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Task Number</Label>
                    <Input value={form.task_number} onChange={(e) => setForm({ ...form, task_number: e.target.value })} className="rounded-sm" data-testid="project-task-number-input" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {sortByLabel(P_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="project-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {sortStrings(P_PRIORITIES).map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Tech Team</Label>
                    <Input value={form.tech_team} onChange={(e) => setForm({ ...form, tech_team: e.target.value })} className="rounded-sm" data-testid="project-tech-team-input" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Func Team</Label>
                    <Input value={form.func_team} onChange={(e) => setForm({ ...form, func_team: e.target.value })} className="rounded-sm" data-testid="project-func-team-input" />
                  </div>
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Project Manager <span className="text-red-600">*</span></Label>
                  <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="project-manager-select"><SelectValue placeholder="Select project manager" /></SelectTrigger>
                    <SelectContent>
                      {managers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" data-testid="project-start-date-input" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">ETA</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" data-testid="project-eta-input" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Revised ETA</Label>
                    <Input type="date" value={form.revised_eta} onChange={(e) => setForm({ ...form, revised_eta: e.target.value })} className="rounded-sm" data-testid="project-revised-eta-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">FY Lic Cost $</Label>
                    <Input type="number" step="0.01" value={form.fy_lic_cost} onChange={(e) => setForm({ ...form, fy_lic_cost: e.target.value })} className="rounded-sm" data-testid="project-fy-lic-cost-input" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Imp Cost $</Label>
                    <Input type="number" step="0.01" value={form.imp_cost} onChange={(e) => setForm({ ...form, imp_cost: e.target.value })} className="rounded-sm" data-testid="project-imp-cost-input" />
                  </div>
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Comments</Label>
                  <Textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} className="rounded-sm" rows={2} data-testid="project-comments-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} data-testid="save-project-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          )}
        </div>

        {/* One row: Entity → Initiative → Project is the cascade order
            described above, then Schedule (independent of it) and Status.
            Labels drop the "All"/"Any" prefix now that each trigger is
            small; the dimension name alone is the unselected state, same
            idea as an unlabelled placeholder. flex-wrap is just a safety
            net for narrow viewports, not the intended layout. */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={entityFilter} onValueChange={onEntityChange}>
            <SelectTrigger className="rounded-sm w-28" data-testid="project-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Entity</SelectItem>
              {entityFilterOptions.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={initiativeFilter} onValueChange={onInitiativeChange}>
            <SelectTrigger className="rounded-sm w-36" data-testid="project-initiative-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Initiative</SelectItem>
              {initiativeFilterOptions.map((i) => <SelectItem key={i._id} value={i._id}>{i.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={onProjectChange}>
            <SelectTrigger className="rounded-sm w-36" data-testid="project-project-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Project</SelectItem>
              {projectFilterOptions.map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scheduleFilter} onValueChange={setScheduleFilter}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="project-filter-schedule"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/* SCHEDULE_FILTERS is shared with the Tasks page (lib/ageing.js)
                  — its "all" label stays "Any schedule state" there; overriding
                  the displayed text only here, not the shared constant, keeps
                  Tasks' own filter unaffected. */}
              {SCHEDULE_FILTERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.id === "all" ? "Schedule" : f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="rounded-sm w-28" data-testid="project-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Status</SelectItem>
              {sortByLabel(statusFilterOptions, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-4 py-6 md:px-8 trk-fade-in">
        <DataTable
          toolbarStart={
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search projects…"
              label="Search projects"
              testid="projects-search"
            />
          }
          storageKey="projects.tableLayout"
          testIdPrefix="projects"
          columns={COLUMN_DEFS}
          rows={filtered}
          rowKey={(p) => p._id}
          pageSize={20}
          emptyMessage="No projects match this filter."
          cellFor={renderCell}
        />
      </div>

      <ReportIssueModal
        open={issueModal.open}
        onOpenChange={(v) => setIssueModal((s) => ({ ...s, open: v }))}
        entityType="project"
        entityId={issueModal.entityId}
        entityLabel={issueModal.entityLabel}
      />

      <BulkUploadProjectsModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        initiatives={initiatives}
        managers={managers}
        projects={items}
        existingTasks={allTasks}
        users={users}
        onDone={load}
      />
    </AppLayout>
  );
}
